'use client'

import { useEffect, useRef } from 'react'

import Script from 'next/script'

import { useGTMConsentState } from '@b-stock/fe-core/bstock-react'

import serializeForScriptTag from '@/helpers/serializeForScriptTag'

export type PendoConfig = Record<
  string,
  Record<string, string | number | boolean | undefined>
>

/**
 * Pendo Tracking Script (Consent-Gated) - App Router Version
 *
 * This component loads Pendo's user behavior analytics and in-app guidance system.
 * It requires 'analytics_storage' consent before loading the tracking script.
 *
 * Pendo provides:
 * - User behavior tracking
 * - Product analytics
 * - In-app messaging and guidance
 * - Feature adoption tracking
 *
 * NOTE: Once loaded, Pendo's tracking and initialization cannot be easily
 * reversed. The library modifies the global window object and begins tracking
 * user interactions immediately.
 *
 * TODO: Look into whether this can be imported from a library/package rather
 * than a remote script.
 */
export default function PendoTrackingScript({
  pendoApiKey,
  pendoConfig,
  nonce,
}: {
  pendoApiKey: string
  pendoConfig: PendoConfig
  nonce?: string
}) {
  const consentState = useGTMConsentState()
  const prevConsentRef = useRef<boolean | null | undefined>(
    consentState.analytics_storage
  )

  // Once this script tag is inserted into the DOM, there is no clean way to stop
  // these user tracking scripts once they have started, so we must force a page
  // reload when consent is withdrawn
  useEffect(() => {
    const currentConsent = consentState.analytics_storage
    const previousConsent = prevConsentRef.current

    // Trigger reload if consent is withdrawn (true -> false)
    if (previousConsent === true && currentConsent === false) {
      console.warn('[PendoTrackingScript] reload due to withdrawn consent')
      window.location.reload()
    }

    prevConsentRef.current = currentConsent
  }, [consentState.analytics_storage])

  // Require analytics_storage consent before rendering this script tag
  if (!consentState.analytics_storage) {
    return null
  }

  return (
    <Script
      id="pendo-tracking"
      nonce={nonce}
      dangerouslySetInnerHTML={{
        __html: `
          (function(apiKey) {
            (function(p, e, n, d, o) {
              var v, w, x, y, z;
              o = p[d] = p[d] || {};
              o._q = o._q || [];
              v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
              for (w = 0, x = v.length; w < x; ++w)(function(m) {
                o[m] = o[m] || function() {
                  o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0)));
                };
              })(v[w]);
              y = e.createElement(n);
              y.async = !0;
              y.src = 'https://cdn.pendo.io/agent/static/' + apiKey + '/pendo.js';
              z = e.getElementsByTagName(n)[0];
              z.parentNode.insertBefore(y, z);
            })(window, document, 'script', 'pendo');

            pendo.initialize(${serializeForScriptTag(pendoConfig)});
          })(${serializeForScriptTag(pendoApiKey)});
        `,
      }}
    />
  )
}
